import { Fragment, useMemo, type FC } from 'react';

import { ThrowError } from '#apps/client-web/app/components/throw-error';
import { routes } from '#apps/client-web/app/routes';
import { FixtureTypeLabel } from '#apps/client-web/features/audit-form-water-fixture/constants';

import {
  type AuditWaterFixtureFormEntry,
  type FixtureType,
} from '#packages/data-collection/models/audit-water-fixture-form';
import { type ViewModelProps } from '#packages/web/types/view-model-props';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { EmptyPage, LoadingPage } from '#packages/web/layouts/page/templates';
import { Fab } from '#packages/web/foundations/buttons/fab';

import { type AuditFormWaterFixturePageViewModel } from './audit-form-water-fixture-page.view-model';
import { AuditFormWaterFixturePageRenderer } from './audit-form-water-fixture-page.renderer';

export type AuditFormWaterFixturePageContainerProps =
  ViewModelProps<AuditFormWaterFixturePageViewModel> & LoggingContext;

export const AuditFormWaterFixturePageContainer: FC<
  AuditFormWaterFixturePageContainerProps
> = ({ viewModel }) => {
  const { auditId, auditFormQuery, thumbnailFileIds, thumbnailMap, onCreate } =
    viewModel;

  const groups = useMemo(() => {
    const entries = auditFormQuery.data?.entries ?? [];

    // Group by section; entries without a section go into 'unspecified'
    const grouped: Record<
      string,
      { entry: AuditWaterFixtureFormEntry; globalIndex: number }[]
    > = {};
    entries.forEach((entry, globalIndex) => {
      const key = entry.section ?? 'unspecified';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({ entry, globalIndex });
    });

    return Object.entries(grouped).map(([section, groupEntries]) => ({
      id: section,
      label: section === 'unspecified' ? 'Unspecified' : section,
      entries: groupEntries.map(({ entry, globalIndex }) => ({
        link: routes.audit.getEnergyAuditWaterFixtureFormEntryPath(
          auditId!,
          globalIndex,
        ),
        entry,
        fixtureTypeLabel: FixtureTypeLabel[entry.fixtureType as FixtureType],
        thumbnail: (() => {
          const fileId = thumbnailFileIds[globalIndex];
          return fileId ? (thumbnailMap[fileId] ?? null) : null;
        })(),
      })),
    }));
  }, [auditFormQuery.data, auditId, thumbnailFileIds, thumbnailMap]);

  const isLoading = auditFormQuery.isLoading;
  const isEmpty = auditFormQuery.isSuccess && groups.length < 1;
  const hasData = !isLoading && !isEmpty;

  return (
    <Fragment>
      {isLoading && <LoadingPage />}
      {isEmpty && <EmptyPage onActionButtonClick={onCreate} />}
      {hasData && <AuditFormWaterFixturePageRenderer groups={groups} />}
      {hasData && <Fab onClick={onCreate} />}
      {auditFormQuery.isError && <ThrowError error={auditFormQuery.error} />}
    </Fragment>
  );
};
